

export const CATEGORY = {
  TECHNICAL: 'technical',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
}

export const CONSENT_CATEGORIES = [
  {
    key: CATEGORY.TECHNICAL,
    required: true,
    titleKey: 'cookieCategoryTechnicalTitle',
    descriptionKey: 'cookieCategoryTechnicalDescription',
  },
  {
    key: CATEGORY.ANALYTICS,
    required: false,
    titleKey: 'cookieCategoryAnalyticsTitle',
    descriptionKey: 'cookieCategoryAnalyticsDescription',
  },
  {
    key: CATEGORY.MARKETING,
    required: false,
    titleKey: 'cookieCategoryMarketingTitle',
    descriptionKey: 'cookieCategoryMarketingDescription',
  },
]

export const COOKIE_LIST_VERSION = '2026-08-29.1'

export const BANNER_TEXT_VERSION = '2026-08-28.1'

export const COOKIE_REGISTRY = [
  {
    name: 'renew_consent',
    match: 'exact',
    vendor: 'Renew',
    category: CATEGORY.TECHNICAL,
    firstParty: true,
    retention: { value: 400, unit: 'days' },
    retentionKey: 'cookieRetentionConsent',
    purposeKey: 'cookiePurposeConsent',
    addedAt: '2026-08-28',
  },
  {
    name: '_ga',
    match: 'prefix',
    vendor: 'Google Analytics 4',
    category: CATEGORY.ANALYTICS,
    firstParty: true,
    retention: { value: 2, unit: 'years' },
    retentionKey: 'cookieRetentionGa',
    purposeKey: 'cookiePurposeGa',
    addedAt: '2026-08-28',
  },
  {
    name: '_hjSessionUser_',
    match: 'prefix',
    vendor: 'Contentsquare (Hotjar)',
    category: CATEGORY.ANALYTICS,
    firstParty: true,
    retention: { value: 365, unit: 'days' },
    retentionKey: 'cookieRetentionHjSessionUser',
    purposeKey: 'cookiePurposeHjSessionUser',
    addedAt: '2026-08-29',
  },
  {
    name: '_hjSession_',
    match: 'prefix',
    vendor: 'Contentsquare (Hotjar)',
    category: CATEGORY.ANALYTICS,
    firstParty: true,
    retention: { value: 30, unit: 'minutes' },
    retentionKey: 'cookieRetentionHjSession',
    purposeKey: 'cookiePurposeHjSession',
    addedAt: '2026-08-29',
  },
  {
    name: '_gcl_au',
    match: 'exact',
    vendor: 'Google Ads',
    category: CATEGORY.MARKETING,
    firstParty: true,
    retention: { value: 90, unit: 'days' },
    retentionKey: 'cookieRetentionGclAu',
    purposeKey: 'cookiePurposeGclAu',
    addedAt: '2026-08-28',
  },
  {
    // Not observed on staging even with full consent - see docs/faq/analytics-tracking.md
    name: '_fbp',
    match: 'exact',
    vendor: 'Meta Pixel',
    category: CATEGORY.MARKETING,
    firstParty: true,
    retention: { value: 90, unit: 'days' },
    retentionKey: 'cookieRetentionFbp',
    purposeKey: 'cookiePurposeFbp',
    addedAt: '2026-08-28',
  },
  {
    name: 'IDE',
    match: 'exact',
    vendor: 'Google Ads (DoubleClick)',
    category: CATEGORY.MARKETING,
    firstParty: false,
    retention: { value: 13, unit: 'months' },
    retentionKey: 'cookieRetentionIde',
    purposeKey: 'cookiePurposeIde',
    addedAt: '2026-08-29',
  },
  {
    name: 'test_cookie',
    match: 'exact',
    vendor: 'Google Ads (DoubleClick)',
    category: CATEGORY.MARKETING,
    firstParty: false,
    retention: { value: 15, unit: 'minutes' },
    retentionKey: 'cookieRetentionTestCookie',
    purposeKey: 'cookiePurposeTestCookie',
    addedAt: '2026-08-29',
  },
]

// Vendors that used to be injected from this repo and now live as tags inside the GTM
// container. Our code loads none of them: what holds them back is the Consent Mode state
// pushed before the container, plus the consent setting on each tag in GTM. The list is kept
// for two jobs - the weekly audit uses it to tell "a container tag fired without consent"
// apart from "an unknown domain turned up", and it is the checklist to reconcile against the
// full tag export from the marketer (a tag nobody told us about is invisible here).
// `signals` is what the tag reads by itself; `additionalCheck` is what the container was
// configured to do on top of it. The two answer different questions and the audit needs both:
// a tag with signals may legitimately ping before a decision (cookieless, Consent Mode doing
// its job), while a tag with `additionalCheck: true` may not appear at all - if it does, the
// check was removed, misconfigured, or the container was never published.
//
// This field describes the live container, not what it ought to be. Update it after a GTM
// change is published, never before - a `true` written in advance turns the audit into a
// document that agrees with itself.
export const CONTAINER_TAG_DOMAINS = [
  {
    vendor: 'Google Analytics 4',
    domains: ['www.google-analytics.com', 'analytics.google.com', 'region1.google-analytics.com'],
    categories: [CATEGORY.ANALYTICS],
    signals: ['analytics_storage'],
    // Built-in consent checks only, no additional check - fires cookieless while denied.
    additionalCheck: false,
    addedAt: '2026-08-31',
  },
  {
    vendor: 'Google Ads (DoubleClick)',
    domains: [
      'googleads.g.doubleclick.net',
      'td.doubleclick.net',
      'www.googleadservices.com',
      'pagead2.googlesyndication.com',
    ],
    categories: [CATEGORY.MARKETING],
    signals: ['ad_storage', 'ad_user_data', 'ad_personalization'],
    // Built-in consent checks only, no additional check - fires cookieless while denied.
    additionalCheck: false,
    addedAt: '2026-08-31',
  },
  {
    vendor: 'Meta Pixel',
    domains: ['connect.facebook.net', 'capi-automation.s3.us-east-2.amazonaws.com'],
    categories: [CATEGORY.MARKETING],
    // Non-Google: reads no Consent Mode signal on its own, so the tag needs an Additional
    // consent check in GTM. Without one it fires on refusal and nothing here prevents it.
    signals: [],
    additionalCheck: false,
    addedAt: '2026-08-31',
  },
  {
    vendor: 'Contentsquare (Hotjar)',
    domains: [
      'static.hj.contentsquare.net',
      'static.hotjar.com',
      'script.hotjar.com',
      'content.hotjar.io',
      'metrics.hotjar.io',
    ],
    categories: [CATEGORY.ANALYTICS],
    signals: [],
    additionalCheck: false,
    addedAt: '2026-08-31',
  },
  {
    vendor: 'TikTok',
    domains: ['analytics.tiktok.com'],
    categories: [CATEGORY.MARKETING],
    signals: [],
    additionalCheck: false,
    addedAt: '2026-08-31',
  },
]

export const CONSENT_FREE_EMBEDS = [
  {
    id: 'youtube-nocookie',
    vendor: 'YouTube',
    domain: 'www.youtube-nocookie.com',
    addedAt: '2026-08-28',
  },
]

export const DOMAINS_ALLOWED_WITHOUT_CONSENT = [
  // The GTM container. It loads for everyone by design - it sets no cookie of its own and
  // carries no payload until a tag inside it passes its consent check. Only the container
  // file is free: see CONSENT_FREE_PATHS, everything else on this host is a tag talking.
  'www.googletagmanager.com',
  // Own infrastructure
  'renew-eu.com',
  'api.renew-eu.com',
  'frontend.renew.hdit.info',
  'backend.renew.hdit.info',
  'frontend.renew.nwdev.net',
  'backend.renew.nwdev.net',
  // Functional third parties: no cookies of ours, needed for the site to work
  'cdnjs.cloudflare.com',
  'api.novaposhta.ua',
  'api.bigdatacloud.net',
  'wft-geo-db.p.rapidapi.com',
  'api.instagram.com',
  'graph.instagram.com',
]

// A host can serve both the gate and the thing behind it: www.googletagmanager.com hands out
// the container from /gtm.js and takes GA4 hits on /g/collect. Only the listed path prefixes
// are allowed before a decision - anything else on that host is a tag that fired too early.
export const CONSENT_FREE_PATHS = {
  // /gtm.js is the container. /gtag/js is the gtag library, and Consent Mode is implemented
  // inside that same file - it has to load before any tag can learn about the denial, and it
  // sets no cookie and sends no identifier while storage is denied. Both are the gate.
  // Everything else on this host is a tag talking: /g/collect, /a, /td and friends.
  'www.googletagmanager.com': ['/gtm.js', '/gtag/js'],
}

// Outbound navigation targets: an href or window.open issues no request until the
// visitor clicks it, so these are not gated - but they still have to be declared.
export const LINK_ONLY_DOMAINS = [
  'skinon.com.ua',
  'www.instagram.com',
  'www.facebook.com',
  't.me',
  'wa.me',
]

// External domains that are in the code but approved by nobody. They do not fail the
// build (they predate the registry) yet they are not allowed to stay either - each one
// is a request that leaves the visitor's IP with a third party before any consent.
export const KNOWN_EXTERNAL_DEBT = [
  {
    domain: 'www.meme-arsenal.com',
    where: 'src/components/misc/popup-wrap.vue',
    reason: 'Placeholder image left over from development',
  },
  {
    domain: 'picsum.photos',
    where: 'src/components/popups/instagram-post/index.vue',
    reason: 'Placeholder image left over from development',
  },
  {
    domain: 'backend.alphascience.hdit.info',
    where: 'src/modules/pages/components/image-right/index.vue',
    reason: 'Patent image still served from the forked project backend',
  },
  {
    domain: 'frontend.alphascience.hdit.info',
    where: 'src/store/modules/setting.ts (GET_INSTAGRAM_GALLERY)',
    reason:
      'Instagram OAuth call carried over from the forked project: foreign redirect_uri, ' +
      'plus a client_secret and an authorization code hardcoded in the source',
  },
  {
    domain: 'embed.tawk.to',
    where: 'public/index.html',
    reason:
      'Tawk chat is commented out. If it comes back it belongs in the GTM container as a ' +
      'tag with a consent check, not inline in this file',
  },
]

const EMPTY_CONSENT = {
  [CATEGORY.TECHNICAL]: true,
  [CATEGORY.ANALYTICS]: false,
  [CATEGORY.MARKETING]: false,
}

export function emptyConsent() {
  return { ...EMPTY_CONSENT }
}

function isGranted(consent, category) {
  return Boolean(consent && consent[category])
}

export function revokedCookies(consent) {
  return COOKIE_REGISTRY.filter((cookie) => {
    if (cookie.firstParty === false) return false
    return cookie.category !== CATEGORY.TECHNICAL && !isGranted(consent, cookie.category)
  })
}

// personalization_storage sits under marketing: it covers personalised recommendations, not
// measurement. Moving it to analytics means moving the cookies behind it in COOKIE_REGISTRY too.
// functionality_storage and security_storage are granted in the default block and never change,
// so they are deliberately absent here - an update repeating them would say nothing.
export function consentModeSignals(consent) {
  const analytics = isGranted(consent, CATEGORY.ANALYTICS) ? 'granted' : 'denied'
  const marketing = isGranted(consent, CATEGORY.MARKETING) ? 'granted' : 'denied'

  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    personalization_storage: marketing,
  }
}

// Strips the identifiers out of Google Ads requests while ad_storage is denied, so a refusal
// costs conversion modelling rather than the whole hit.
export function adsDataRedaction(consent) {
  return !isGranted(consent, CATEGORY.MARKETING)
}
